<template>
  <div class="cursor-container" :class="{ 'fallback-mode': useFallback, 'system-cursor': useSystemCursor }">
    <!-- Primary cursor with images -->
    <div class="cursor" ref="cursor" v-if="!useSystemCursor">
      <!-- Only show images if not in fallback mode -->
      <template v-if="!useFallback">
        <img :src="darkLogoSrc" alt="360° Cursor Black" class="cursor-image dark-logo" 
             :style="{ opacity: isDarkBackground ? 0 : 1 }"
             @error="handleImageError" />
        <img :src="lightLogoSrc" alt="360° Cursor White" class="cursor-image light-logo" 
             :style="{ opacity: isDarkBackground ? 1 : 0 }"
             @error="handleImageError" />
      </template>
      
      <!-- Fallback rectangle cursor -->
      <div v-if="useFallback" class="fallback-cursor" 
           :class="{ 'dark-bg': isDarkBackground }"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomCursor',
  data() {
    return {
      hoverables: [],
      // Store paths as strings instead of requiring directly
      darkLogoPath: '@/assets/images/360_icon_black.png',
      lightLogoPath: '@/assets/images/360_icon_white.png',
      darkLogoSrc: null,
      lightLogoSrc: null,
      currentLogoSrc: null,
      isDarkBackground: false,
      lastBgColor: 'rgb(255, 255, 255)',
      lastBrightness: 255,
      lastElementType: 'div',
      useFallback: false,
      useSystemCursor: false,
      imagesLoaded: 0,
      imageLoadAttempts: 0
    }
  },
  created() {
    // Setup initial fallback
    this.useFallback = true; // Default to fallback until images are loaded
    
    // Check if custom cursor is supported
    this.checkCursorSupport();
    
    // Try to load images safely
    this.loadCursorImages();
  },
  mounted() {
    if (this.useSystemCursor) {
      return; // Don't initialize custom cursor logic if using system cursor
    }
    
    // Initially position the cursor in the center of the screen
    const cursor = this.$refs.cursor;
    if (cursor) {
      cursor.style.left = `${window.innerWidth / 2}px`;
      cursor.style.top = `${window.innerHeight / 2}px`;
    }
    
    // Track cursor position and detect background
    document.addEventListener('mousemove', this.handleMouseMove);
    
    // Click effect
    document.addEventListener('mousedown', () => {
      const cursor = this.$refs.cursor;
      if (cursor) cursor.classList.add('cursor-click');
    });
    
    document.addEventListener('mouseup', () => {
      const cursor = this.$refs.cursor;
      if (cursor) cursor.classList.remove('cursor-click');
    });
    
    // Add a window.setTimeout to ensure all elements are loaded before initializing hoverables
    window.setTimeout(() => {
      this.refreshHoverElements();
    }, 1000);
    
    // Add a mutation observer to detect DOM changes and refresh hoverable elements
    this.setupMutationObserver();
    
    // Listen for cookie consent events to refresh hoverables
    window.addEventListener('cookieConsentRendered', this.refreshHoverElements);
  },
  methods: {
    checkCursorSupport() {
      // Feature detection for pointer-events
      if (typeof document.body.style.pointerEvents === 'undefined') {
        this.useSystemCursor = true;
        return;
      }
      
      // Check if browser is mobile/touch-only
      if (('ontouchstart' in window) && !window.matchMedia('(pointer: fine)').matches) {
        this.useSystemCursor = true;
        return;
      }
    },
    
    loadCursorImages() {
      try {
        // Try to dynamically import images (handles compile-time errors)
        import(/* webpackChunkName: "cursor-images" */ '@/assets/images/360_icon_black.png')
          .then(module => {
            this.darkLogoSrc = module.default;
            this.imagesLoaded++;
            if (this.imagesLoaded === 2) this.useFallback = false;
          })
          .catch(() => {
            // Keep fallback mode active
          });
          
        import(/* webpackChunkName: "cursor-images" */ '@/assets/images/360_icon_white.png')
          .then(module => {
            this.lightLogoSrc = module.default;
            this.imagesLoaded++;
            if (this.imagesLoaded === 2) this.useFallback = false;
          })
          .catch(() => {
            // Keep fallback mode active
          });
          
        // Safety timeout - if imports are slow, ensure we have a cursor
        setTimeout(() => {
          if (this.imagesLoaded < 2) {
            // Images taking too long to load, keeping fallback active
          }
        }, 2000);
      } 
      catch (error) {
        // Fallback is already active, so no need to activate it again
      }
    },
    
    handleImageError() {
      this.activateFallback();
    },
    
    activateFallback() {
      if (this.imageLoadAttempts > 2) {
        // If we've tried multiple times, fall back to system cursor
        this.useSystemCursor = true;
        return;
      }
      
      this.useFallback = true;
      this.imageLoadAttempts++;
    },
    
    setupMutationObserver() {
      // Create a mutation observer to watch for DOM changes
      const observer = new MutationObserver(() => {
        // If DOM changes, refresh our hoverable elements
        this.refreshHoverElements();
      });
      
      // Start observing the document with the configured parameters
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
      
      // Store observer reference for cleanup
      this.observer = observer;
    },
    
    refreshHoverElements() {
      // Re-initialize hoverable elements
      this.initializeHoverables();
    },
    
    initializeHoverables() {
      // Get all hoverable elements with a more thorough selector
      this.hoverables = document.querySelectorAll(
        'a, button, .team-card, .project-card, .process-item, .nav-toggle, ' + 
        '.view-more, .social-icon, .social-icons a, .social-icons-outer a, .social-icons-mobile a, ' +  // Added mobile social icons
        '.modal-close, .slide-control, .cta-button, ' +
        '[class*="card"], [class*="overlay"], ' +
        // Cookie consent elements
        '#cc-main .cm__btn, #cc-main .pm__btn, #cc-main .pm__close-btn, ' +
        '#cc-main .cm__footer a, #cc-main .pm__footer a, #cc-main .toggle, ' +
        '[data-cc], [data-cc] *, .cm__btn, .pm__btn, .pm__close-btn'
      );
      
      this.setupHoverListeners();
    },
    
    setupHoverListeners() {
      if (!this.hoverables || !this.hoverables.length) {
        return;
      }
      
      this.hoverables.forEach(el => {
        // First remove any existing listeners to prevent duplicates
        el.removeEventListener('mouseenter', this.handleElementHover);
        el.removeEventListener('mouseleave', this.handleElementLeave);
        
        // Add the listeners back
        el.addEventListener('mouseenter', this.handleElementHover);
        el.addEventListener('mouseleave', this.handleElementLeave);
      });
    },
    
    handleElementHover() {
      const cursor = this.$refs.cursor;
      if (cursor) {
        cursor.classList.add('cursor-hover');
      }
    },
    
    handleElementLeave() {
      const cursor = this.$refs.cursor;
      if (cursor) {
        cursor.classList.remove('cursor-hover');
      }
    },
    
    handleMouseMove(e) {
      if (this.useSystemCursor) return;
      
      const cursor = this.$refs.cursor;
      if (cursor) {
        // Use requestAnimationFrame for smooth updates
        requestAnimationFrame(() => {
          cursor.style.left = `${e.clientX}px`;
          cursor.style.top = `${e.clientY}px`;
        });
      }
      
      // Detect background color under cursor
      this.detectBackgroundColor(e.clientX, e.clientY);
    },
    
    detectBackgroundColor(x, y) {
      // Get element under the cursor
      const element = document.elementFromPoint(x, y);
      if (!element) return;
      
      // Store element type for debugging
      this.lastElementType = element.tagName.toLowerCase();
      
      // COOKIE CONSENT HANDLING: Check if we're over cookie consent elements
      // More comprehensive detection for vanilla-cookieconsent elements  
      const isCookieConsentElement = element.matches('#cc-main, #cc-main *') ||
                                    element.closest('#cc-main') ||
                                    element.id === 'cc-main' ||
                                    element.classList.contains('cm') ||
                                    element.classList.contains('pm') ||
                                    element.closest('.cm') ||
                                    element.closest('.pm');
      
      // DEBUG: Log cookie consent detection (disabled for production)
      // if (isCookieConsentElement) {
      //   console.log('🍪 Cookie consent element detected:', {
      //     element: element,
      //     tagName: element.tagName,
      //     className: element.className,
      //     id: element.id,
      //     textContent: element.textContent?.substring(0, 50)
      //   });
      // }
      
      if (isCookieConsentElement) {
        // Check if it's the modal backdrop/overlay
        const isCookieBackdrop = element.matches('.pm-overlay') ||
                                element.closest('.pm-overlay') ||
                                element.classList.contains('pm-overlay');
        
        if (isCookieBackdrop) {
          // Dark overlay background - use WHITE cursor
          this.isDarkBackground = true;
          this.currentLogoSrc = this.lightLogoSrc;
          this.lastBgColor = 'Cookie Consent: Backdrop';
          this.lastBrightness = 0;
          return;
        }
        
        // Check SPECIFICALLY for Accept All button (primary button)
        const isAcceptAllButton = element.matches('.cm__btn:not(.cm__btn--secondary)') ||
                                 element.closest('.cm__btn:not(.cm__btn--secondary)') ||
                                 element.classList.contains('cm__btn') && !element.classList.contains('cm__btn--secondary') ||
                                 (element.textContent && element.textContent.toLowerCase().trim() === 'accept all');
        
        if (isAcceptAllButton) {
          // Accept All button ONLY - white cursor (dark background)
          this.isDarkBackground = true;
          this.currentLogoSrc = this.lightLogoSrc;
          this.lastBgColor = 'Cookie Consent: Accept All Button';
          this.lastBrightness = 0;
          return;
        }
        
        // ALL OTHER cookie consent elements - black cursor on white background
        // This includes: banner content, secondary buttons, links, modal content, etc.
        this.isDarkBackground = false;
        this.currentLogoSrc = this.darkLogoSrc;
        this.lastBgColor = 'Cookie Consent: Default (Black Cursor)';
        this.lastBrightness = 255;
        return;
      }
      
      // NEW: Check if we're over a project hero section or its title
      const isProjectHero = element.matches('.project-hero') || 
                           element.closest('.project-hero') ||
                           element.matches('.project-title') ||
                           element.closest('.project-hero-overlay');
      
      // NEW: Check if we're over hero sections in other pages (contact, static, workflow)
      const isOtherPageHero = element.matches('.contact-hero, .contact-hero-overlay, .contact-title, .page-hero, .page-hero-overlay, .page-title, .workflow-hero, .workflow-hero-overlay, .workflow-title') || 
                             element.closest('.contact-hero, .contact-hero-overlay, .page-hero, .page-hero-overlay, .workflow-hero, .workflow-hero-overlay') ||
                             element.matches('h1.contact-title, h1.page-title, h1.workflow-title') ||
                             element.closest('.contact-title, .page-title, .workflow-title');
      
      // Check if we're hovering over the Three Sixty logo in the hero section
      const isLogoInHero = element.matches('.threesixty-logo-container, .threesixty-logo') || 
                           element.closest('.threesixty-logo-container') ||
                           element.matches('.threesixty-logo-container a, .threesixty-logo-container img');
      
      if ((isProjectHero || isOtherPageHero) && !isLogoInHero) {
        // Force WHITE cursor over hero/cover without hover effect
        this.isDarkBackground = true;
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Hero Area';
        this.lastBrightness = 0;
        
        // Ensure cursor doesn't change size by removing the hover class
        const cursor = this.$refs.cursor;
        if (cursor) cursor.classList.remove('cursor-hover');
        
        return;
      }
      
      // Special handling for logo in hero - white cursor WITH hover effect
      if (isLogoInHero && (element.closest('.project-hero') || element.closest('.contact-hero') || 
          element.closest('.page-hero') || element.closest('.workflow-hero'))) {
        // Force WHITE cursor with hover effect
        this.isDarkBackground = true;
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Logo in Hero Area';
        this.lastBrightness = 0;
        return;
      }
      
      // NEW: Check for project images in content section that should not show hover state
      const isProjectContentImage = element.matches('.project-image-section img') || 
                                   element.closest('.project-image-section img') ||
                                   element.matches('.image-left img') ||
                                   element.closest('.image-left img') || 
                                   element.matches('.image-right img') ||
                                   element.closest('.image-right img') ||
                                   element.matches('.image-wrapper.full img') ||
                                   element.closest('.image-wrapper.full img');
                                   
      if (isProjectContentImage) {
        // Force WHITE cursor over project content images without hover effect
        this.isDarkBackground = true;
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Project Content Image';
        this.lastBrightness = 0;
        
        // Ensure cursor is not showing hover state
        const cursor = this.$refs.cursor;
        if (cursor) cursor.classList.remove('cursor-hover');
        
        return;
      }
      
      // NEW: Check if we're in a lightbox area but outside the image
      const isLightboxBackground = element.matches('.lightbox') && 
                                  !element.matches('.lightbox img') && 
                                  !element.closest('.lightbox img') &&
                                  !element.matches('.lightbox button') &&
                                  !element.closest('.lightbox button');
      
      if (isLightboxBackground) {
        // Force WHITE cursor on lightbox dark background
        this.isDarkBackground = true;
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Lightbox Background';
        this.lastBrightness = 0;
        return;
      }
      
      // Check for project-info elements
      const isProjectInfoArea = element.matches('.project-info') || 
                                element.closest('.project-info') || 
                                element.matches('.project-info *');
      
      // PROJECT INFO HANDLING: Keep cursor white over project info overlays
      if (isProjectInfoArea) {
        // Force WHITE cursor on project info areas
        this.isDarkBackground = true;
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Project Info Area';
        this.lastBrightness = 0;
        return;
      }
      
      // Check for view-more element within team cards
      const isViewMoreLink = element.matches('.view-more') || 
                            element.closest('.view-more');
      
      // More thorough element checking - check current element and all parents
      const isCloseButton = element.matches('.modal-close') || 
                            element.closest('.modal-close') ||
                            (element.parentElement && element.parentElement.matches('.modal-close'));
      
      const isSocialIcon = element.matches('.social-icon') || 
                          element.closest('.social-icon') ||
                          element.matches('.social-icon svg') ||
                          element.matches('.social-icon path') ||
                          (element.parentElement && element.parentElement.matches('.social-icon')) ||
                          element.closest('.social-icons');
      
      // TEAM CARD VIEW DETAILS: Special handling to keep cursor white
      if (isViewMoreLink && element.closest('.member-overlay')) {
        // Use WHITE logo but ensure hover state is triggered
        this.isDarkBackground = true;
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Team Card: View Details';
        this.lastBrightness = 0;
        return;
      }
      
      // TEAM MODAL HANDLING: Check if team modal is open and handle special areas
      const teamModal = document.querySelector('.team-modal.active');
      if (teamModal) {
        // Check if hovering over external social icons (desktop)
        const isExternalSocialIcon = element.closest('.social-icons-outer') && 
                                    !element.closest('.social-icons-outer').parentElement.matches('.quadrant');
        
        // Check for mobile social icons (now inside the bottom-right quadrant)
        const isMobileSocialIcon = element.closest('.social-icons-mobile');
        
        if (isExternalSocialIcon) {
          // Use WHITE logo on external social icons
          this.isDarkBackground = true;
          this.currentLogoSrc = this.lightLogoSrc;
          this.lastBgColor = 'Team Modal: External Social Icons';
          this.lastBrightness = 0;
          return;
        }
        
        // Social media icons inside the modal (including mobile) and close button should use BLACK cursor
        if (isCloseButton || (isSocialIcon && !isExternalSocialIcon) || isMobileSocialIcon) {
          // Use BLACK logo on social media icons and close button
          this.isDarkBackground = false;
          this.currentLogoSrc = this.darkLogoSrc;
          this.lastBgColor = 'Team Modal: Interactive Elements';
          this.lastBrightness = 255;
          return;
        }
        
        // Check if we're over the reference images area (bottom-left quadrant)
        if (element.closest('.quadrant.bottom-left') || 
            element.matches('.slide img') || 
            element.matches('.slide-caption')) {
          // Use WHITE logo on image area
          this.isDarkBackground = true;
          this.currentLogoSrc = this.lightLogoSrc;
          this.lastBgColor = 'Team Modal: Image Area';
          this.lastBrightness = 0;
          return;
        }
        
        // Check if we're outside the modal content (backdrop area)
        if (element.matches('.team-modal') && !element.closest('.modal-container')) {
          // Use WHITE logo on modal backdrop
          this.isDarkBackground = true;
          this.currentLogoSrc = this.lightLogoSrc;
          this.lastBgColor = 'Team Modal: Backdrop';
          this.lastBrightness = 0;
          return;
        }
      }
      
      // SECOND: Check if we're on the navigation toggle (menu button) or inside the nav area
      const navToggle = element.closest('.nav-toggle');
      const navWrapper = document.querySelector('.nav-wrapper');
      
      if (navToggle) {
        // MODIFIED: Check if the navigation menu is open
        const navMenuActive = document.querySelector('.navigation.active');
        
        if (navMenuActive) {
          // Navigation menu is open - always use BLACK cursor on the toggle
          this.isDarkBackground = false;
          this.currentLogoSrc = this.darkLogoSrc;
          this.lastBgColor = 'Nav Toggle (menu open)';
          this.lastBrightness = 255;
          return;
        }
        // If we're on the nav toggle, check if the navbar is scrolled
        else if (navWrapper && navWrapper.classList.contains('scrolled')) {
          // Navbar has white background - use BLACK logo regardless of the square color
          this.isDarkBackground = false;
          this.currentLogoSrc = this.darkLogoSrc;
          this.lastBgColor = 'Nav Toggle (scrolled)';
          this.lastBrightness = 255;
          return;
        } else {
          // Navbar has transparent background - use WHITE logo regardless of the square color
          this.isDarkBackground = true;
          this.currentLogoSrc = this.lightLogoSrc;
          this.lastBgColor = 'Nav Toggle (not scrolled)';
          this.lastBrightness = 0;
          return;
        }
      }
      
      // Check if element is in the navbar with white background
      if (element.closest('.nav-wrapper.scrolled')) {
        // Always use black cursor on white navbar background
        this.isDarkBackground = false;
        this.currentLogoSrc = this.darkLogoSrc;
        this.lastBgColor = 'Nav Bar (scrolled)';
        this.lastBrightness = 255;
        return;
      }
      
      // NEW: Check if navigation menu is open (cream background)
      if (element.closest('.navigation.active') || element.matches('.navigation.active')) {
        // Always use black cursor in the open navigation menu
        this.isDarkBackground = false;
        this.currentLogoSrc = this.darkLogoSrc;
        this.lastBgColor = 'Navigation Menu Open';
        this.lastBrightness = 255;
        return;
      }
      
      // SECOND: Handle media elements (next priority)
      if (element.tagName === 'IMG' || element.tagName === 'VIDEO' || element.tagName === 'CANVAS') {
        this.isDarkBackground = true; // Use WHITE logo on actual media elements
        this.currentLogoSrc = this.lightLogoSrc;
        this.lastBgColor = 'Media element';
        this.lastBrightness = 0;
        return;
      }
      
      // Check if element is inside or contains media
      const isInMediaContext = 
        element.closest('.video-container, .image-container, .media-section, .hero, .hero-split, .hero-media, .project-media') ||
        element.querySelector('img, video, canvas');
      
      if (isInMediaContext) {
        this.isDarkBackground = false; // Use BLACK logo in media contexts
        this.currentLogoSrc = this.darkLogoSrc;
        this.lastBgColor = 'Media context';
        this.lastBrightness = 255;
        return;
      }
      
      // Get background color
      let bgColor = window.getComputedStyle(element).backgroundColor;
      
      // If transparent, try to find parent with background
      if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
        let parent = element.parentElement;
        let foundColor = false;
        
        while (parent && !foundColor) {
          const parentColor = window.getComputedStyle(parent).backgroundColor;
          if (parentColor !== 'rgba(0, 0, 0, 0)' && parentColor !== 'transparent') {
            bgColor = parentColor;
            foundColor = true;
          }
          parent = parent.parentElement;
        }
        
        if (!foundColor) {
          // Default to white background if no color found
          bgColor = 'rgb(255, 255, 255)';
        }
      }
      
      // Store the detected background color for debugging
      this.lastBgColor = bgColor;
      
      // Determine if background is dark or light
      this.switchLogoBasedOnBackground(bgColor);
    },
    
    switchLogoBasedOnBackground(bgColor) {
      // Extract RGB values
      const rgb = bgColor.match(/\d+/g);
      if (!rgb || rgb.length < 3) return;
      
      // Check specifically for gold accent color
      const isGold = parseInt(rgb[0]) > 150 && parseInt(rgb[1]) > 130 && parseInt(rgb[2]) < 125;
      
      if (isGold) {
        // For gold accent, show white logo
        this.isDarkBackground = true;
        // Don't update currentLogoSrc directly - it might be null
        return;
      }
      
      // Calculate perceived brightness (standard formula)
      const brightness = (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) / 1000;
      
      // Store brightness for debugging
      this.lastBrightness = brightness;
      
      // Increased brightness threshold to 140 (more sensitive to darker backgrounds)
      const isDarkBg = brightness < 140;
      
      // Only update if there's a change to avoid unnecessary re-renders
      if (isDarkBg !== this.isDarkBackground) {
        this.isDarkBackground = isDarkBg;
        
        // No need to update currentLogoSrc here - we use isDarkBackground in the template
      }
    }
  },
  beforeUnmount() {
    // Clean up the mutation observer if it exists
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (!this.useSystemCursor) {
      // Clean up event listeners only if we're using custom cursor
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mousedown', null);
      document.removeEventListener('mouseup', null);
      
      // Remove cookie consent event listener
      window.removeEventListener('cookieConsentRendered', this.refreshHoverElements);
      
      if (this.hoverables) {
        this.hoverables.forEach(el => {
          el.removeEventListener('mouseenter', null);
          el.removeEventListener('mouseleave', null);
        });
      }
    }
  }
}
</script>

<style>
/* Hide the default cursor everywhere including links */
.system-cursor * {
  cursor: auto; /* Reset to default cursor for fallback mode */
}

/* Apply custom cursor only when not using system cursor */
body:not(.system-cursor),
a:not(.system-cursor),
button:not(.system-cursor),
input[type="submit"]:not(.system-cursor),
input[type="button"]:not(.system-cursor),
.clickable:not(.system-cursor),
[role="button"]:not(.system-cursor),
.nav-toggle:not(.system-cursor),
/* Team member specific elements */
.team-member:not(.system-cursor),
.member-card:not(.system-cursor),
.member-overlay:not(.system-cursor),
.view-more:not(.system-cursor),
.team-grid:not(.system-cursor),
/* Additional common interactive elements */
[class*="card"]:not(.system-cursor),
[class*="overlay"]:not(.system-cursor),
.project-card:not(.system-cursor),
.project-item:not(.system-cursor),
[class*="hover"]:not(.system-cursor),
/* Ensure any elements with cursor: pointer are covered */
*[style*="cursor: pointer"]:not(.system-cursor),
*[style*="cursor:pointer"]:not(.system-cursor) {
  cursor: none !important;
}

/* Ensure cursor shows on touch devices */
@media (hover: none) {
  body, a, button {
    cursor: auto !important;
  }
}
</style>

<style scoped>
.cursor-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999999 !important; /* Much higher than cookie consent (999999) */
}

/* Hide cursor container when using system cursor */
.system-cursor {
  display: none;
}

.cursor {
  position: absolute;
  width: 30px;
  height: 30px;
  transform: translate(-50%, -50%);
  transition: width 0.2s, height 0.2s, transform 0.2s;
  z-index: 9999999 !important; /* Much higher than cookie consent (999999) */
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cursor-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.2s ease; /* Add transition for smooth color change */
}

/* Fallback rectangle cursor styling */
.fallback-cursor {
  width: 100%;
  height: 100%;
  border: 3px solid #000;
  box-sizing: border-box;
  transition: border-color 0.2s ease;
}

.fallback-cursor.dark-bg {
  border-color: #fff;
}

.cursor-click {
  transform: translate(-50%, -50%) scale(0.6);
}

.cursor-hover {
  transform: translate(-50%, -50%) scale(0.7) !important; /* Make the effect more noticeable */
  transition: transform 0.2s ease !important;
}

/* Hide on touch devices */
@media (hover: none) {
  .cursor {
    display: none;
  }
}
</style> 